import { useEffect, useState, useRef, forwardRef, useCallback } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { isJapanese } from 'wanakana';
import Tippy from '@tippyjs/react';
import { handleKanaInputChange } from '../../utils/input';

const KANJI_REGEX = /[\u4E00-\u9FFF\u3400-\u4DBF]/;

const InputSection = forwardRef(({ checkSubmitted, userInput, inputResults, setUserInput, handleCheck, handleNext, isCorrect, isIgnored, isAnyModalOpen = false, inputMode = 'mixed', autoConvertToKana = true, reducedAnimations = false }, ref) => {
  const [shake, setShake] = useState(false);
  const kanjiPromptShown = useRef(false);
  const [tooltipMessage, setTooltipMessage] = useState('');
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  InputSection.displayName = 'InputSection';

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    handleResize(); // Initialize
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmissionAttempt = useCallback(() => {
    let trimmedInput = userInput.trim();

    if (!trimmedInput) {
      setShake(true);
      setTimeout(() => setShake(false), 600);
      showFeedback('Please type your answer.');
      return;
    }
    if (trimmedInput.endsWith('n')) trimmedInput = trimmedInput.replace(/n$/, 'ん');

    if (!isJapanese(trimmedInput)) {
      setShake(true);
      setTimeout(() => setShake(false), 600);
      showFeedback('Please use Japanese characters.');
      return;
    }

    // If preliminary validation passes, call the parent's function.
    handleCheck();
  }, [userInput, handleCheck]);

  const showFeedback = (message, duration = 1500) => {
    setTooltipMessage(message);
    setIsTooltipVisible(true);
    setShake(true);
    setTimeout(() => {
      setShake(false);
      setIsTooltipVisible(false);
    }, duration);
  };

  const handleKeyPress = useCallback(
    (e) => {
      // Ignore auto-repeat: a held Enter would submit and then immediately
      // advance, skipping past the graded card before it can be read.
      if (e.key === 'Enter' && !e.shiftKey && !e.repeat && !isAnyModalOpen) {
        e.preventDefault();
        if (checkSubmitted) {
          handleNext();
        } else if (document.activeElement === ref.current) {
          handleSubmissionAttempt(); // Use the new local handler
        }
      }
    },
    [checkSubmitted, handleNext, handleSubmissionAttempt, ref, isAnyModalOpen]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  useEffect(() => {
    if (checkSubmitted) setIsTooltipVisible(false);
  }, [checkSubmitted]);

  useEffect(() => {
    if (isAnyModalOpen) setIsTooltipVisible(false);
  }, [isAnyModalOpen]);

  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = `${ref.current.scrollHeight}px`;
    }
    if (!userInput) {
      kanjiPromptShown.current = false;
    }
  }, [userInput, ref]);

  const barStyle = isIgnored
    ? 'border-yellow-500'
    : checkSubmitted
    ? isCorrect
      ? 'border-green-500'
      : 'border-red-500'
    : 'border-border focus-within:border-primary';
  const buttonStyle = isIgnored
    ? 'bg-yellow-500 hover:bg-yellow-600'
    : checkSubmitted
    ? isCorrect
      ? 'bg-green-500 hover:bg-green-600'
      : 'bg-red-500 hover:bg-red-600'
    : 'bg-primary hover:bg-primary/90';
  const shakeClass = shake ? 'animate-shake' : '';

  return (
    <Tippy
      content={tooltipMessage}
      visible={isTooltipVisible}
      placement="bottom"
      trigger="manual" // We control visibility with the `visible` prop
      arrow
    >
      <div className="w-full">
        <div
          className={`flex items-stretch bg-background-secondary backdrop-blur-sm rounded-2xl border-2 transition-all duration-300 ${barStyle} ${shakeClass} shadow-lg`}
        >
          <div className="flex-grow min-h-[68px] flex items-center">
            {checkSubmitted ? (
              <div className="flex flex-wrap items-center gap-1 p-2" lang="ja">
                {inputResults.map((result, index) => {
                  if (isIgnored) {
                    return (
                      <span key={index} className="px-3 py-1.5 rounded-lg text-[1.35rem] sm:text-[1.6rem] font-semibold bg-yellow-500/20 text-yellow-700 dark:text-yellow-300">
                        {result.text}
                      </span>
                    );
                  }
                  if (result.unchecked) {
                    return (
                      <span key={index} className="px-3 py-1.5 rounded-lg text-[1.35rem] sm:text-[1.6rem] font-semibold bg-gray-500/20 text-gray-500 dark:text-gray-400">
                        {result.text}
                      </span>
                    );
                  }
                  if (!result.correct && result.expected) {
                    return (
                      <Tippy key={index} content={`Expected: ${result.expected}`} placement="bottom" arrow trigger="mouseenter click" touch={['hold', 100]}>
                        <span className={`px-3 py-1.5 rounded-lg text-[1.35rem] sm:text-[1.6rem] font-semibold bg-red-500/20 text-red-700 dark:text-red-300 cursor-pointer ${!reducedAnimations ? 'animate-gentle-bounce' : ''}`}>
                          {result.text}
                        </span>
                      </Tippy>
                    );
                  }
                  return (
                    <span key={index} className={`px-3 py-1.5 rounded-lg text-[1.35rem] sm:text-[1.6rem] font-semibold ${result.correct ? 'bg-green-500/20 text-green-700 dark:text-green-300' : 'bg-red-500/20 text-red-700 dark:text-red-300'}`}>
                      {result.text}
                    </span>
                  );
                })}
              </div>
            ) : (
              <textarea
                ref={ref}
                lang="ja"
                className="w-full h-full bg-transparent p-4 text-[1.35rem] sm:text-[1.6rem] text-content-primary placeholder-content-secondary/60 placeholder:text-[1.35rem] sm:placeholder:text-[1.6rem] focus:outline-none resize-none overflow-hidden border-none"
                value={userInput}
                onChange={(e) => {
                  if (inputMode === 'kana') {
                    if (!kanjiPromptShown.current && KANJI_REGEX.test(e.target.value)) {
                      kanjiPromptShown.current = true;
                      showFeedback('Kanji detected! In Kana mode, only hiragana/katakana is accepted — kanji will not match. Switch to Mixed or Strict Kanji mode in Session Settings to use kanji.', 15000);
                    } else if (!KANJI_REGEX.test(e.target.value)) {
                      setIsTooltipVisible(false);
                      kanjiPromptShown.current = false;
                    }
                  }
                  if (autoConvertToKana) {
                    handleKanaInputChange(setUserInput, ref)(e);
                  } else {
                    setUserInput(e.target.value);
                  }
                }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                inputMode="text"
                placeholder={isMobile ? "Type here..." : "Type what you hear..."}
                rows={1}
                style={{ minHeight: '60px' }}
              />
            )}
          </div>

          <button
            onClick={checkSubmitted ? handleNext : handleSubmissionAttempt}
            className={`session-check-button flex-shrink-0 w-16 m-1.5 text-white rounded-xl transition-colors duration-300 ${buttonStyle}`}
            aria-label={checkSubmitted ? 'Next' : 'Submit'}
          >
            <ArrowRightIcon className="h-7 w-7 mx-auto" />
          </button>
        </div>
      </div>
    </Tippy>
  );
});

export default InputSection;
